import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaRocket, FaBriefcase, FaCode, FaUsers, FaPlay, FaArrowRight, FaStar, FaCheckCircle, FaGraduationCap } from "react-icons/fa";
import { Link } from "react-router-dom";
import backgroundImage from "../../assets/background.jpg";
import { useTheme } from "../Theme/ThemeContext";

// Animation variants
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } }
};

export default function HeroSection() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { currentTheme } = useTheme();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative overflow-hidden min-h-[100vh] flex items-center">
      {/* Animated Background */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundBlendMode: "overlay",
        }}
      >
        {/* Dark overlay with color tint */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-indigo-950/85 to-violet-950/90" />
        
        {/* Animated mesh gradient with parallax */}
        <motion.div 
          className="absolute inset-0 opacity-60"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 20% 40%, rgba(56, 189, 248, 0.2), transparent),
              radial-gradient(ellipse 60% 40% at 80% 60%, rgba(167, 139, 250, 0.18), transparent),
              radial-gradient(ellipse 40% 30% at 40% 80%, rgba(236, 72, 153, 0.12), transparent)
            `,
            x: mousePosition.x * 0.5,
            y: mousePosition.y * 0.5
          }}
        />
      </div>

      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      {/* Floating Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-20 left-[15%] w-72 h-72 bg-gradient-to-br from-cyan-500/25 to-blue-500/15 rounded-full blur-3xl"
          animate={{ y: [0, -30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          style={{ x: mousePosition.x * -0.3, y: mousePosition.y * -0.3 }}
        />
        <motion.div 
          className="absolute bottom-20 right-[10%] w-96 h-96 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/15 rounded-full blur-3xl"
          animate={{ y: [0, 40, 0], scale: [1, 0.9, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{ x: mousePosition.x * 0.2, y: mousePosition.y * 0.2 }}
        />
        <motion.div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-pink-500/15 to-transparent rounded-full blur-3xl"
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full ${i % 3 === 0 ? 'bg-cyan-400/60' : i % 3 === 1 ? 'bg-violet-400/60' : 'bg-pink-400/60'}`}
            style={{
              width: `${3 + (i % 3)}px`,
              height: `${3 + (i % 3)}px`,
              left: `${10 + i * 12}%`,
              top: `${15 + (i % 4) * 20}%`,
            }}
            animate={{
              y: [-15, 15, -15],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: 3 + i * 0.4,
              repeat: Infinity,
              delay: i * 0.2,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Text Content */}
          <motion.div 
            className="text-white"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {/* Badge */}
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-3 glass rounded-full px-5 py-2.5 mb-8 border border-white/10 shadow-lg"
            >
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="w-3 h-3 text-amber-400" />
                ))}
              </div>
              <span className="text-sm font-medium text-white/90">Golang Bootcamp Now Live</span>
              <motion.div animate={{ y: [-2, 2, -2] }} transition={{ duration: 1.5, repeat: Infinity }}>
                <FaRocket className="w-4 h-4 text-amber-400" />
              </motion.div>
            </motion.div>

            {/* Main Heading */}
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-display font-bold mb-6 leading-[1.05] tracking-tight"
            >
              Master Golang
              <motion.span 
                className={`block mt-3 bg-gradient-to-r ${currentTheme.colors.gradientText} bg-clip-text text-transparent`}
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                style={{ backgroundSize: '200% auto' }}
              >
                From Zero to Hero
              </motion.span>
            </motion.h1>

            {/* Description */}
            <motion.p 
              variants={fadeInUp}
              className="text-lg sm:text-xl text-neutral-300 mb-10 leading-relaxed max-w-xl"
            >
              Learn with comprehensive theory, video lessons, MCQs, and real coding practice. 
              Execute code with hidden test cases and get instant feedback to land your dream job.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link to="/courses">
                <motion.button
                  type="button"
                  className={`group relative inline-flex items-center justify-center gap-2.5 font-semibold rounded-2xl bg-gradient-to-r ${currentTheme.colors.gradient} text-white px-8 py-4 text-lg w-full sm:w-auto shadow-2xl ${currentTheme.colors.glow} overflow-hidden`}
                  whileHover={{ scale: 1.03, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <FaPlay className="w-4 h-4" />
                  Start Learning Free
                  <FaArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </Link>

              <Link to="/jobs">
                <motion.button
                  type="button"
                  className="group inline-flex items-center justify-center gap-2.5 font-semibold rounded-2xl glass border border-white/20 hover:bg-white/10 hover:border-white/30 px-8 py-4 text-lg w-full sm:w-auto transition-all duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FaBriefcase className="w-4 h-4" />
                  Browse Jobs
                  <FaArrowRight className="w-4 h-4 opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                </motion.button>
              </Link>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-4">
              {[
                { icon: FaCheckCircle, text: "Real-time Execution", colorClass: "text-theme-primary" },
                { icon: FaCheckCircle, text: "Hidden Test Cases", colorClass: "text-theme-secondary" },
                { icon: FaCheckCircle, text: "Placement Support", colorClass: "text-theme-accent" },
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  className="flex items-center gap-2 text-sm text-neutral-300"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + i * 0.1 }}
                >
                  <item.icon className={`w-4 h-4 ${item.colorClass}`} />
                  <span>{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Feature Cards */}
          <motion.div 
            className="relative"
            variants={fadeInRight}
            initial="hidden"
            animate="visible"
          >
            <motion.div 
              className={`relative z-10 rounded-3xl p-6 sm:p-8 border-2 ${currentTheme.colors.border} shadow-2xl ${currentTheme.colors.glow}`}
              style={{
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 27, 75, 0.95) 50%, rgba(20, 20, 50, 0.95) 100%)',
              }}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              {/* Card Header */}
              <div className={`flex items-center gap-3 mb-6 pb-4 border-b ${currentTheme.colors.border}`}>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${currentTheme.colors.gradient} flex items-center justify-center shadow-lg ${currentTheme.colors.glow}`}>
                  <FaGraduationCap className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">What You'll Get</h3>
                  <p className="text-theme-primary opacity-80 text-sm">Complete learning experience</p>
                </div>
              </div>

              {/* Feature Cards */}
              <div className="space-y-4">
                <FeatureCard icon={<FaCode className="text-white text-lg" />} title="Hands-on Coding Practice" subtitle="Real IDE with test case execution" colors="from-cyan-500 to-blue-500" index={0} />
                <FeatureCard icon={<FaUsers className="text-white text-lg" />} title="Placement Assistance" subtitle="Resume review & mock interviews" colors="from-violet-500 to-fuchsia-500" index={1} />
                <FeatureCard icon={<FaBriefcase className="text-white text-lg" />} title="Job Opportunities" subtitle="500+ verified openings for freshers" colors="from-pink-500 to-rose-500" index={2} />
              </div>

              {/* Stats Bar */}
              <div className={`mt-6 pt-4 border-t ${currentTheme.colors.border} grid grid-cols-3 gap-4`}>
                {[
                  { value: "50+", label: "Chapters", colorClass: "text-theme-primary" },
                  { value: "100+", label: "Problems", colorClass: "text-theme-secondary" },
                  { value: "10K+", label: "Students", colorClass: "text-theme-accent" },
                ].map((stat, i) => (
                  <motion.div key={i} className="text-center" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 + i * 0.1 }}>
                    <div className={`text-xl font-bold ${stat.colorClass}`}>{stat.value}</div>
                    <div className="text-xs text-gray-300">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Decorative elements */}
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-gradient-to-br from-cyan-500/25 to-blue-500/15 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-br from-violet-500/25 to-fuchsia-500/15 rounded-full blur-3xl" />
            
            {/* Floating badge */}
            <motion.div 
              className="absolute -top-4 -right-4 px-4 py-2 rounded-full border-2 border-fuchsia-500/40 shadow-xl shadow-fuchsia-500/20 z-20"
              style={{
                background: 'linear-gradient(135deg, rgba(192, 38, 211, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%)',
              }}
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <span className="text-sm font-bold text-white">🔥 Popular</span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-neutral-50 dark:from-neutral-900 to-transparent pointer-events-none" />

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
          <motion.div 
            className="w-1 h-2 bg-white/50 rounded-full"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
}

// Enhanced Feature Card Component with solid readable backgrounds
function FeatureCard({ icon, title, subtitle, colors, index }) {
  // Map colors to solid background variants
  const bgMap = {
    'from-cyan-500 to-blue-500': 'bg-cyan-950/90 border-cyan-500/40 hover:border-cyan-400',
    'from-violet-500 to-fuchsia-500': 'bg-violet-950/90 border-violet-500/40 hover:border-violet-400',
    'from-pink-500 to-rose-500': 'bg-pink-950/90 border-pink-500/40 hover:border-pink-400',
  };
  const cardBg = bgMap[colors] || 'bg-slate-800/90 border-white/20';

  return (
    <motion.div 
      className={`group flex items-center gap-4 p-5 rounded-2xl ${cardBg} border-2 transition-all duration-300 cursor-pointer shadow-xl`}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.5 + index * 0.1 }}
      whileHover={{ x: 8, scale: 1.02 }}
    >
      <div className={`w-14 h-14 bg-gradient-to-br ${colors} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-bold text-lg">{title}</h4>
        <p className="text-gray-300 text-sm mt-0.5">{subtitle}</p>
      </div>
      <FaArrowRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
    </motion.div>
  );
}
